import type { UserOnboarding } from "@cohesive/api-client";
import type { Moment } from "./types";

/**
 * A moment is seen once its key is stamped in the record's `steps`, the map
 * the API merges per key. Nothing else spells the prefix. A `version` above 1
 * folds into the key, so a rewritten moment fires again for everyone.
 */
const PREFIX = "coach:";

export function seenKey(moment: Pick<Moment, "key" | "version">): string {
  const version = moment.version ?? 1;
  return version > 1
    ? `${PREFIX}${moment.key}:v${version}`
    : `${PREFIX}${moment.key}`;
}

export function isSeen(
  record: UserOnboarding | undefined,
  moment: Pick<Moment, "key" | "version">,
): boolean {
  return seenKey(moment) in (record?.steps ?? {});
}

/** The keys of every seen moment in the registry. */
export function seenSet(
  record: UserOnboarding | undefined,
  moments: readonly Moment[],
): Set<string> {
  const seen = new Set<string>();
  for (const m of moments) if (isSeen(record, m)) seen.add(m.key);
  return seen;
}

export function seenPatch(
  moment: Pick<Moment, "key" | "version">,
  now: Date = new Date(),
): UserOnboarding {
  return { steps: { [seenKey(moment)]: now.toISOString() } };
}

/** Blank every coachmark stamp; an empty value deletes the key server side. */
export function resetPatch(record: UserOnboarding | undefined): UserOnboarding {
  const steps: Record<string, string> = {};
  for (const key of Object.keys(record?.steps ?? {})) {
    if (key.startsWith(PREFIX)) steps[key] = "";
  }
  return { steps };
}
